import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import CustomNavbar from "../components/Navbar";
import { useParams, Navigate } from 'react-router-dom';
import Footer from "../components/Footer";
import SubscriptionForm from "../components/SubscriptionForm";
import { LEGACY_NEWS_ID_TO_UNIQUE } from "../data/legacyNewsIdToUnique";
import Seo from "../components/Seo";
import NotFoundPage from "./NotFoundPage";
import { PAGE_SEO } from "../seo/pages";

function mapNewsRow(row) {
    const r = row.attributes || row;
    return {
        id: row.id,
        unique: r.unique ?? row.unique,
        title: r.Title,
        content: r.Content,
        date: r.Date,
        category: r.CategoryName || "News",
    };
}

const NewsInnerPage = () => {
    const { slug } = useParams();
    const [newsItem, setNewsItem] = useState(null);
    const [latestNews, setLatestNews] = useState([]);
    const [redirectTo, setRedirectTo] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (!slug) return;

        let cancelled = false;

        const fetchNewsItem = async () => {
            setNewsItem(null);
            setRedirectTo(null);
            setLoadFailed(false);

            const isLegacyId = /^\d+$/.test(slug);
            if (isLegacyId) {
                const legacyUnique = LEGACY_NEWS_ID_TO_UNIQUE[Number(slug)];
                if (legacyUnique) {
                    setRedirectTo(`/news/${legacyUnique}`);
                    return;
                }
            }

            const base = process.env.REACT_APP_BACKEND_API_URL;
            const filterUrl = isLegacyId
                ? `${base}/api/news?filters[id][$eq]=${encodeURIComponent(slug)}&populate=*`
                : `${base}/api/news?filters[unique][$eq]=${encodeURIComponent(slug)}&populate=*`;

            try {
                const res = await axios.get(filterUrl);
                if (cancelled) return;

                const row = res.data?.data?.[0];
                if (!row) {
                    setLoadFailed(true);
                    return;
                }

                const mapped = mapNewsRow(row);

                if (isLegacyId && mapped.unique) {
                    setRedirectTo(`/news/${mapped.unique}`);
                    return;
                }

                setNewsItem(mapped);
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching specific news item:', error.response ? error.response.data : error.message);
                    setLoadFailed(true);
                }
            }
        };

        fetchNewsItem();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    useEffect(() => {
        const fetchLatestNews = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/api/news?sort=Date:desc&pagination[limit]=6&populate=*`);
                if (res.data && res.data.data) {
                    const latest = res.data.data.map((item) => {
                        const r = item.attributes || item;
                        const unique = r.unique ?? item.unique;
                        const id = item.id;
                        return {
                            id,
                            title: r.Title,
                            date: r.Date,
                            category: r.CategoryName || "News",
                            link: unique ? `/news/${unique}` : `/news/${id}`,
                        };
                    });
                    setLatestNews(latest);
                }
            } catch (error) {
                console.error('Error fetching latest news:', error.response ? error.response.data : error.message);
            }
        };

        fetchLatestNews();
    }, []);

    if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
    }

    if (loadFailed) {
        return <NotFoundPage />;
    }

    return (
        <div>
            <Seo
                title={newsItem ? `${newsItem.title} | Q-Gold Resources` : PAGE_SEO.news.title}
                description={newsItem ? newsItem.title : PAGE_SEO.news.description}
                path={`/news/${slug}`}
            />
            <CustomNavbar />
            <section className="news-inner-page">

                <Container>
                    <Row>
                        <Col md={8} className="news-inner-wrapper">
                            {newsItem ? (
                                <>
                                    <h3 className="fw-bold">{newsItem.title}</h3>
                                    <p className="text-muted">{newsItem.date} | {newsItem.category}</p>
                                    <div className="news-content" dangerouslySetInnerHTML={{ __html: newsItem.content }} />
                                </>
                            ) : (
                                <p>Loading article...</p>
                            )}
                            <SubscriptionForm
                                titleColor="#0b1c3b"
                                textColor="#595959"
                                subscribedTextColor="#d2b521"
                            />
                        </Col>
                        <Col md={4} className="news-inner-latest-wrapper">
                            <div className="latest-news-sidebar">
                                <h3 className="mb-4">Latest News</h3>
                                <ul className="list-unstyled">
                                    {latestNews
                                        .filter((news) => news.title !== newsItem?.title)
                                        .map((news, idx) => (
                                        <li key={idx} className="mb-5">
                                            <h4 className="text-muted">{news.title}</h4>
                                            <p>{news.date} | {news.category}</p>
                                            <a href={news.link} className="read-more-btn">
                                                Read More <span className="arrow-icon">➔</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            <Footer />
        </div>
    );
};

export default NewsInnerPage;
